import { useState } from 'react';

import { useFieldArray, useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Arrangement } from '../../types';

import { travelLinesTableColumDefinitions } from './utils/utils';
import { findIndexFromFieldsArray } from 'components/shared/CRUD/utils/utils';

import TableCRUDLayout from 'components/shared/CRUD/TableCRUDLayout';
import { useEditPageContext } from 'components/shared/CRUD/components/InnerPage/EditPageContext';
import withFieldArrayDrawer from 'components/shared/CRUD/components/InnerPage/components/withFieldArrayDrawer';
import { fieldArrayContext } from 'components/shared/CRUD/components/InnerPage/hooks/useFieldArrayContext';
import useRemoveUncommitted from 'components/shared/CRUD/components/InnerPage/hooks/useRemoveUncommitted';

import EditTravelLine from './editTravelLines/EditTravelLine';

interface Props {
  closeInnerView: () => void;
  rowData?: Arrangement;
}

const EditTravelSetLines = withFieldArrayDrawer(
  EditTravelLine,
  'travelSetLines'
);

const TravelLinesTable: React.FC<Props> = () => {
  const { isEditable } = useEditPageContext();
  const { t } = useTranslation('common');
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);

  const { control } = useFormContext();

  const {
    fields: travelSetLines,
    append,
    remove,
    update,
    replace
  } = useFieldArray({
    control: control,
    name: 'travelSetLines'
  });

  const onAddNew = () => {
    // add a new empty record to the fieldArray
    append({ isCommitted: false, valid: { from: null, to: null } });
    setIsDrawerOpen(true);
  };

  const onDelete = (fieldIds) => {
    remove(findIndexFromFieldsArray(fieldIds[0], travelSetLines));
  };

  useRemoveUncommitted(travelSetLines, isDrawerOpen, remove);

  const onCloseInnerView = () => {
    setIsDrawerOpen(false);
  };

  return (
    <fieldArrayContext.Provider
      value={{ fields: travelSetLines, append, remove, update, replace }}
    >
      <TableCRUDLayout
        columns={travelLinesTableColumDefinitions}
        rows={travelSetLines}
        FormComponent={EditTravelSetLines}
        getRowId={(travelLine) => travelLine.id}
        dynamicHeight={true}
        disableActions={!isEditable}
        onAddClick={onAddNew}
        onDeleteClick={onDelete}
        confirmationTitle={t('deleteDialog.confirmationTitle')}
        confirmationBody={t('deleteDialog.confirmationBody')}
        onClose={onCloseInnerView}
        shouldRenderToolBar={false}
      />
    </fieldArrayContext.Provider>
  );
};

export default TravelLinesTable;
